'use client';

import { Box, Button, Stack, Typography } from '@mui/material';
import { useAccentColors } from '@/contexts/AccentColorsContext';
import { DEFAULT_ACCENT_COLOR } from '@/components/theme';

function ColorRow(props: {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  onReset: () => void;
  inputId: string;
}) {
  const { label, value, onChange, onReset, inputId } = props;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ minWidth: { sm: 160 } }}>
        {label}
      </Typography>
      <Box
        component="input"
        id={inputId}
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        sx={{
          width: 56,
          height: 36,
          padding: 0,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          cursor: 'pointer',
          bgcolor: 'background.paper',
        }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
        {value}
      </Typography>
      <Button
        size="small"
        variant="outlined"
        onClick={onReset}
        disabled={value.toLowerCase() === DEFAULT_ACCENT_COLOR.toLowerCase()}
      >
        Reset
      </Button>
    </Stack>
  );
}

export const AppearanceAccentSettings: React.FC = () => {
  const {
    lightAccent,
    darkAccent,
    setLightAccent,
    setDarkAccent,
    resetLightAccent,
    resetDarkAccent,
    resetAllAccents,
  } = useAccentColors();

  const bothDefault =
    lightAccent.toLowerCase() === DEFAULT_ACCENT_COLOR.toLowerCase() &&
    darkAccent.toLowerCase() === DEFAULT_ACCENT_COLOR.toLowerCase();

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Accent colors
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose the primary (buttons, app bar, highlights) for light and dark mode. Saved only on this browser.
      </Typography>
      <ColorRow
        label="Light mode accent"
        inputId="fsm-accent-light-input"
        value={lightAccent}
        onChange={setLightAccent}
        onReset={resetLightAccent}
      />
      <ColorRow
        label="Dark mode accent"
        inputId="fsm-accent-dark-input"
        value={darkAccent}
        onChange={setDarkAccent}
        onReset={resetDarkAccent}
      />
      <Button size="small" variant="text" onClick={resetAllAccents} disabled={bothDefault}>
        Reset both to default
      </Button>
    </Box>
  );
};
